import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { db, auth, functions } from '../firebase/config';
import { AGREEMENT_VERSION, AGREEMENT_TEXT } from '../config/salesAgreement';
import { appConfirm } from './AppAlert';

// Firestore reads can stall without rejecting (multi-tab cache lease on iOS
// Safari), which left this gate on "Loading..." forever — cap every await so
// a stall becomes a visible, retryable error instead
function withTimeout(promise, ms = 15000) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => {
      const e = new Error('Request timed out');
      e.name = 'TimeoutError';
      reject(e);
    }, ms)),
  ]);
}

// Blocks sales_rep / sales_manager users until they sign the current
// confidentiality & non-solicitation agreement. Declining deactivates the
// account and signs them out. Non-sales roles pass through with zero
// Firestore reads. Mounted inside PrivateRoute, so auth is already resolved.
export default function AgreementGate({ children }) {
  const [status,    setStatus]    = useState('checking'); // checking | clear | gate | error
  const [typedName, setTypedName] = useState('');
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState('');
  const navigate = useNavigate();

  const check = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) { setStatus('clear'); return; }
      const tr = await withTimeout(user.getIdTokenResult());
      const role = tr.claims.role || null;
      if (role !== 'sales_rep' && role !== 'sales_manager') { setStatus('clear'); return; }
      const snap = await withTimeout(getDoc(doc(db, 'salesTeam', user.uid)));
      if (snap.exists() && snap.data().agreementVersion === AGREEMENT_VERSION) {
        setStatus('clear');
      } else {
        setStatus('gate');
      }
    } catch (e) {
      // A stalled read is a connectivity problem, not an unsigned agreement —
      // show retry. Anything else fails closed to the gate for sales roles.
      if (e?.name === 'TimeoutError') { setStatus('error'); return; }
      setStatus('gate');
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  async function sign() {
    if (!typedName.trim() || busy) return;
    setBusy(true);
    setErr('');
    try {
      await httpsCallable(functions, 'recordAgreementDecision')({
        decision: 'signed', typedName: typedName.trim(), version: AGREEMENT_VERSION,
      });
      setStatus('checking');
      await check();
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  }

  async function decline() {
    if (busy) return;
    if (!(await appConfirm('Declining deactivates your account and signs you out — continue?', { danger: true }))) return;
    setBusy(true);
    setErr('');
    try {
      await httpsCallable(functions, 'recordAgreementDecision')({
        decision: 'declined', version: AGREEMENT_VERSION,
      });
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  if (status === 'clear') return children;

  if (status === 'checking') {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1123', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
        Loading...
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1123', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, color: '#fff', fontFamily: 'Arial, sans-serif', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 30 }}>📡</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>Couldn't reach the server — check your connection.</div>
        <button onClick={() => { setStatus('checking'); check(); }}
          style={{ padding: '12px 28px', background: '#e91e8c', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          ↻ Retry
        </button>
      </div>
    );
  }

  // Full-screen agreement — no dismiss, no escape
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, background: '#0f1123', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', padding: 16 }}>
      <div style={{ background: '#1a1d2e', borderRadius: 14, border: '1px solid rgba(255,255,255,0.12)', width: 560, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', padding: '22px 20px', color: '#fff' }}>
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>📜 Sales Team Agreement</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 14 }}>
          You must review and sign before using RoofBeam.
        </div>

        <div style={{ flex: 1, minHeight: 120, overflowY: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '14px 14px', fontSize: 12.5, lineHeight: 1.65, whiteSpace: 'pre-wrap', color: 'rgba(255,255,255,0.85)', marginBottom: 14 }}>
          {AGREEMENT_TEXT}
        </div>

        <label style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 5 }}>
          Type your full legal name to sign
        </label>
        <input
          value={typedName}
          onChange={e => setTypedName(e.target.value)}
          placeholder="Full legal name"
          style={{ width: '100%', padding: '12px 13px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, color: '#fff', fontSize: 15, boxSizing: 'border-box', marginBottom: 12 }}
        />

        {err && <div style={{ color: '#e74c3c', fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <button onClick={sign} disabled={!typedName.trim() || busy}
          style={{ width: '100%', padding: 14, background: typedName.trim() && !busy ? '#2ecc71' : 'rgba(255,255,255,0.08)', color: typedName.trim() && !busy ? '#fff' : 'rgba(255,255,255,0.35)', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 15, cursor: typedName.trim() && !busy ? 'pointer' : 'not-allowed', marginBottom: 8 }}>
          {busy ? '⏳ Saving...' : '✍️ Sign & Continue'}
        </button>
        <button onClick={decline} disabled={busy}
          style={{ width: '100%', padding: 11, background: 'none', color: 'rgba(231,76,60,0.85)', border: '1px solid rgba(231,76,60,0.35)', borderRadius: 9, fontSize: 13, cursor: 'pointer' }}>
          Decline
        </button>
      </div>
    </div>
  );
}
